import {
  Box,
  Button,
  Collapsible,
  FieldWrapper,
  FlexBox,
  Icon,
  InputSelectionList,
  InputSelectionListChangeEvent,
  Text,
} from '@dixa/lib-shared-habla';
import type { FilterControlOperator } from 'lib-analytics-types';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { articles } from 'utils/elevio';

import { AnalyticsDocumentationButton } from '../../../../../documentation/analyticsDocumentationButton';
import { getOperatorAssets } from '../../../../utils/filters/filterAssets';
import { AnalyticsFilterItem } from '../item/analyticsFilterItem';

import styles from './analyticsFilterOptions.module.scss';

type AnalyticsFilterOptionsProps<TOperator extends FilterControlOperator> = {
  /** The available operators to select. */
  operators: readonly TOperator[];
  /** The default selected operator. */
  defaultOperator?: TOperator;
  /** The currently selected operators. */
  selectedOperator?: TOperator;
  /** Whether no values have been selected. */
  isMissingSelectedValues: boolean;
  /**
   * Whether the parent dropdown is open. Used to close the collapsible view
   * when the dropdown closes, and to open it when a new operator was selected.
   */
  isDropdownOpen?: boolean;
  /** Whether the collapsible view should be open by default. */
  isDefaultOpen?: boolean;
  /** Callback for when the filter operator is changed. */
  onChangeOperator: (operator: TOperator) => void;
  /** Callback for clearing the current filter. */
  onClear?: VoidFunction;
};

const AnalyticsFilterOptions = <TOperator extends FilterControlOperator>({
  operators,
  defaultOperator,
  selectedOperator,
  isMissingSelectedValues,
  isDropdownOpen = false,
  isDefaultOpen = false,
  onChangeOperator,
  onClear,
}: AnalyticsFilterOptionsProps<TOperator>): JSX.Element => {
  const [isOpen, setIsOpen] = useState(isDefaultOpen);

  // Handles closing the collapsible view when the dropdown closes, and opening it when the dropdown
  // is opened and a different operator than the default one is selected.
  useEffect(() => {
    if (!isDefaultOpen) {
      setIsOpen(isDropdownOpen && selectedOperator !== defaultOperator);
    }

    // We only want to set the state when the dropdown is opened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDefaultOpen, isDropdownOpen]);

  const orderedOperators = useMemo(
    () =>
      [...operators].sort(
        (a, b) =>
          getOperatorAssets(a).orderIndex - getOperatorAssets(b).orderIndex,
      ),
    [operators],
  );

  const handleChangeOperator = useCallback(
    (event: InputSelectionListChangeEvent<TOperator>) => {
      if (event.selectedItem && event.selectedItem.value !== selectedOperator) {
        onChangeOperator(event.selectedItem.value);
      }
    },
    [onChangeOperator, selectedOperator],
  );

  const errorMessage = useMemo(
    () =>
      selectedOperator &&
      getOperatorAssets(selectedOperator).requiresValues &&
      isMissingSelectedValues
        ? 'Please select at least one value to filter.'
        : undefined,
    [isMissingSelectedValues, selectedOperator],
  );

  const isClearable =
    onClear &&
    (!isMissingSelectedValues ||
      (selectedOperator && selectedOperator !== defaultOperator));

  return (
    <Collapsible
      isOpen={isOpen}
      header={
        <FlexBox alignItems="center" justifyContent="space-between">
          <FlexBox gap="200" alignItems="center">
            <Text.ExternalLink
              className={styles.link}
              as="div"
              onClick={() => setIsOpen(!isOpen)}
            >
              <FlexBox gap="200" alignItems="center">
                <Icon name={isOpen ? 'angle-down' : 'angle-right'} />
                Advanced options
              </FlexBox>
            </Text.ExternalLink>

            <AnalyticsDocumentationButton
              type="article"
              articleId={articles.analytics.advancedFilters}
            />
          </FlexBox>

          {isClearable && (
            <Button variant="secondary" onClick={onClear}>
              Clear
            </Button>
          )}
        </FlexBox>
      }
    >
      <Box padding="300 0 0 0">
        <FieldWrapper
          label="Match..."
          meta={{
            error: errorMessage,
          }}
          noAlert={!errorMessage}
        >
          <InputSelectionList<TOperator>
            value={selectedOperator ? [selectedOperator] : undefined}
            placeholder="Select an operator"
            disabled={!isOpen}
            onChange={handleChangeOperator}
            getItemTitleByValue={(operator) =>
              operator ? getOperatorAssets(operator).label : null
            }
            dropdownProps={{ fixed: true, className: styles.dropdown }}
            data-testid="analytics-filter-operator-input"
          >
            {orderedOperators.map((operator) => (
              <AnalyticsFilterItem
                key={operator}
                value={operator}
                label={getOperatorAssets(operator).label}
              />
            ))}
          </InputSelectionList>
        </FieldWrapper>
      </Box>
    </Collapsible>
  );
};

const _AnalyticsFilterOptions = memo(
  AnalyticsFilterOptions,
) as typeof AnalyticsFilterOptions;

export { _AnalyticsFilterOptions as AnalyticsFilterOptions };
